import { createRouter, createRootRouteWithContext, createRoute, redirect } from "@tanstack/react-router";
import { RootComponent } from "./routes/root";
import { IndexComponent } from "./routes/index";
import { MetricsComponent } from "./routes/metrics";
import { OverviewComponent } from "./routes/overview";
import { AddUserComponent } from "./routes/addUser";
import { NotFoundComponent } from "./routes/notFound";
import { UnauthorizedComponent } from "./routes/unauthorized";
import { ErrorComponent } from "./routes/error";
import { HowToGuideComponent } from "./routes/howToGuide";
import { userAirlinesQueryOptions, type UserAirlinesResponse } from "./hooks/useUserAirlinesQuery";
import { queryClient } from "./providers/QueryProvider";
import type { AuthInfo } from "./hooks/useAuth";
import { requireAuth } from "./utils/auth";

type RouterContext = {
  auth: AuthInfo;
};

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  beforeLoad: ({ context, location }) => {
    const { error } = context.auth;
    if (error && location.pathname !== "/unauthorized") {
      throw redirect({ to: "/unauthorized" });
    }
  },
});

type MetricsSearchParams = {
  airlineCode?: string;
};

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexComponent,
  beforeLoad: ({ context }) => {
    const { isAirlineUser, airlineCode } = context.auth;
    requireAuth(context.auth);

    if (isAirlineUser) {
      if (airlineCode) {
        throw redirect({ to: "/metrics" });
      }
      throw redirect({ to: "/unauthorized" });
    }

    throw redirect({ to: "/overview" });
  },
});

const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overview",
  component: OverviewComponent,
  beforeLoad: ({ context }) => {
    const { isAirlineUser, airlineCode } = context.auth;
    requireAuth(context.auth);

    if (isAirlineUser) {
      if (airlineCode) {
        throw redirect({ to: "/metrics" });
      }
      throw redirect({ to: "/unauthorized" });
    }
  },
});

const metricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/metrics",
  component: MetricsComponent,
  validateSearch: (search: Record<string, unknown>): MetricsSearchParams => ({
    airlineCode: typeof search.airlineCode === "string" ? search.airlineCode : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const { isAirlineUser, airlineCode } = context.auth;
    requireAuth(context.auth);

    if (isAirlineUser) {
      if (!airlineCode) throw redirect({ to: "/unauthorized" });
      if (search.airlineCode) throw redirect({ to: "/metrics", search: {} });
      return;
    }

    if (!search.airlineCode) throw redirect({ to: "/overview" });

    const { airlines } = await queryClient.ensureQueryData<UserAirlinesResponse>(userAirlinesQueryOptions);
    if (!airlines.some(({ code }) => code === search.airlineCode)) {
      throw redirect({ to: "/overview" });
    }
  },
});

const addUserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/add-user",
  component: AddUserComponent,
  beforeLoad: ({ context }) => {
    const { isAdmin } = context.auth;
    if (!isAdmin) {
      throw redirect({ to: "/overview" });
    }
  },
});

const howToGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/how-to-guide",
  component: HowToGuideComponent,
  beforeLoad: ({ context }) => {
    requireAuth(context.auth);
  },
});

const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/unauthorized",
  component: UnauthorizedComponent,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  overviewRoute,
  metricsRoute,
  addUserRoute,
  howToGuideRoute,
  unauthorizedRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundComponent,
  defaultErrorComponent: ErrorComponent,
  context: {
    auth: undefined!,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
